import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, setDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { LabelSchema } from '../../types/labeling';
import { GlobalCategory } from '../../types/labelSchema';
import SchemaForm from '../../components/SchemaForm';
import CategoryManager from '../../components/CategoryManager';
import ObjectEditModal from '../../components/ObjectEditModal';
import { useGlobalCategories, useCreateGlobalCategory } from '../../hooks/useGlobalCategories';
import { useObjectCategories } from '../../hooks/useObjectCategories';
import { useParentCategories } from '../../hooks/useParentCategories';
import { useLabelAttributes } from '../../hooks/useLabelAttributes';
import { createTestData } from '../../utils/createTestData';

export default function DetectionSetup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'schemas' | 'objects'>('schemas');
  const [schemas, setSchemas] = useState<LabelSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSchema, setEditingSchema] = useState<LabelSchema | null>(null);
  const [editingObject, setEditingObject] = useState<GlobalCategory | null>(null);
  const [showObjectEditModal, setShowObjectEditModal] = useState(false);
  const [editingCombinedObject, setEditingCombinedObject] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [archiveFilter, setArchiveFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [parentCategoryFilter, setParentCategoryFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'global' | 'schema'>('all');

  const { categories: globalObjects, loading: objectsLoading, error: objectsError, updateCategory, deleteCategory } = useGlobalCategories();
  const { createCategory, loading: creatingObject } = useCreateGlobalCategory();
  const { categories: objectCategories, loading: categoriesLoading, error: categoriesError, createCategory: createObjectCategory, updateCategory: updateObjectCategory, deleteCategory: deleteObjectCategory } = useObjectCategories();
  const { categories: parentCategories, loading: parentLoading, error: parentError, createCategory: createParentCategory, updateCategory: updateParentCategory, deleteCategory: deleteParentCategory } = useParentCategories();
  
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  
  // Manual reload function
  const reloadSchemas = () => {
    setLoading(true);
    setError(null);
    setSchemas([]);
  };

  // Load schemas
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    console.log('🔍 Loading schemas from labelSchemas collection...');
    const schemasQuery = collection(db, 'labelSchemas');
    
    // Reset state when starting
    setLoading(true);
    setError(null);
    
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ Schema loading timeout after 10 seconds');
      setLoading(false);
      setError('Loading timeout - please check your connection');
    }, 10000);

    const unsubscribe = onSnapshot(
      schemasQuery, 
      (snapshot) => {
        console.log('📊 Schemas snapshot received:', {
          size: snapshot.size,
          empty: snapshot.empty,
          docs: snapshot.docs.length
        });
        
        clearTimeout(timeoutId);
        setError(null);
        
        const schemasData: LabelSchema[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          console.log('📄 Schema doc:', { id: doc.id, data });
          schemasData.push({
            id: doc.id,
            ...data
          } as LabelSchema);
        });
        
        console.log('✅ Loaded schemas:', schemasData);
        setSchemas(schemasData);
        setLoading(false);
      },
      (err) => {
        console.error('❌ Error loading schemas:', err);
        clearTimeout(timeoutId);
        setError(`Failed to load schemas: ${err.message}`);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []); // Empty dependency array - run once on mount

  // Handle createSchema parameter from URL
  useEffect(() => {
    const createSchema = searchParams.get('createSchema');
    if (createSchema === 'true') {
      setActiveTab('schemas');
      setEditingSchema(null);
      setEditingObject(null);
      setShowCreateModal(true);
      // Clear the parameter from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const filteredSchemas = schemas.filter(schema => {
    const matchesSearch = schema.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         schema.guidelines?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (archiveFilter === 'active') return matchesSearch && !schema.archived;
    if (archiveFilter === 'archived') return matchesSearch && schema.archived;
    return matchesSearch;
  });

  // Build combined list: global objects + schema label classes
  type CombinedObject = {
    id: string;
    name: string;
    displayName: string;
    description?: string;
    color?: string;
    shapes?: string[];
    category?: string; // NEW: top-level category (optional)
    subcategory?: string; // RENAMED FROM category: subcategory
    hotkey?: string;
    tags?: string[];
    usageCount?: number;
    createdBy?: string;
    createdAt?: any;
    updatedAt?: any;
    icon?: string;
    labelType: 'Global Object' | string; // schema name for schema items
    source: 'global' | 'schema';
    active?: boolean;
  };

  const formatDate = (value?: any) => {
    if (!value) return '—';
    try {
      const date = value.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleDateString();
    } catch (err) {
      return '—';
    }
  };

  // Export schemas to Excel/CSV
  const exportSchemasToExcel = () => {
    if (schemas.length === 0) {
      alert("No schemas to export");
      return;
    }

    // Collect all unique field names from all schemas
    const allFields = new Set<string>();
    schemas.forEach((schema: any) => {
      Object.keys(schema).forEach(key => allFields.add(key));
    });

    // Define column order (important fields first, then others)
    const columnOrder = [
      'id', 'name', 'version', 'scope', 'guidelines', 'archived',
      'includeGlobalCategories', 'selectedGlobalCategories',
      'classes', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'
    ];

    // Add any remaining fields not in the order
    const orderedFields = [...columnOrder.filter(f => allFields.has(f)), ...Array.from(allFields).filter(f => !columnOrder.includes(f))];

    // Create CSV header
    const headers = orderedFields.map(field => {
      const displayName = field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      return displayName;
    });

    // Create CSV rows
    const rows = schemas.map((schema: any) => {
      return orderedFields.map(field => {
        let value = schema[field];
        
        // Handle nested objects
        if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
          if (field === 'classes' && Array.isArray(value)) {
            // Special handling for classes array - expand into readable format
            value = value.map((cls: any) => {
              const parts = [];
              if (cls.name) parts.push(`name:${cls.name}`);
              if (cls.displayName) parts.push(`display:${cls.displayName}`);
              if (cls.id) parts.push(`id:${cls.id}`);
              if (cls.category) parts.push(`category:${cls.category}`);
              if (cls.shapes) parts.push(`shapes:[${cls.shapes.join(',')}]`);
              if (cls.hotkey) parts.push(`hotkey:${cls.hotkey}`);
              return `{${parts.join('; ')}}`;
            }).join(' | ');
          } else if (field === 'selectedGlobalCategories' && Array.isArray(value)) {
            value = value.join('; ');
          } else {
            value = JSON.stringify(value);
          }
        }
        
        // Handle arrays (non-classes)
        if (Array.isArray(value) && field !== 'classes') {
          value = value.join('; ');
        }
        
        // Handle booleans
        if (typeof value === 'boolean') {
          value = value ? 'Yes' : 'No';
        }
        
        // Handle dates/timestamps
        if (value && (value.toDate || (typeof value === 'number' && value > 1000000000000))) {
          try {
            const date = value.toDate ? value.toDate() : new Date(value);
            value = date.toISOString();
          } catch {
            value = String(value);
          }
        }
        
        // Convert to string and escape commas/quotes
        const stringValue = String(value || '');
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `schemas_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const combinedObjects: CombinedObject[] = [
    ...globalObjects.map((o) => ({
      id: o.id,
      name: o.name,
      displayName: o.displayName,
      description: o.description,
      color: o.color,
      shapes: o.shapes,
      category: o.category, // NEW: top-level category
      subcategory: o.subcategory || o.category, // RENAMED: subcategory (fallback to old category for migration)
      hotkey: o.hotkey,
      tags: o.tags,
      attributes: o.attributes || [],
      usageCount: o.usageCount,
      createdBy: o.createdBy,
      createdAt: o.createdAt,
      updatedAt: o.updatedAt,
      icon: o.icon,
      labelType: 'Global Object' as const,
      source: 'global' as const,
      active: o.active !== false,
    })),
    ...schemas
      .filter((s) => !s.archived)
      .flatMap((s) =>
        (s.classes || []).map((cls) => ({
          id: `${s.id}:${cls.id}`,
          name: cls.name,
          displayName: cls.displayName,
          description: cls.description,
          color: undefined,
          shapes: cls.shapes,
          category: (cls as any).category, // NEW: top-level category
          subcategory: (cls as any).subcategory || (cls as any).category, // RENAMED: subcategory (fallback for migration)
          hotkey: cls.hotkey,
          tags: (cls as any).tags,
          usageCount: undefined,
          createdBy: s.createdBy,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          labelType: s.name,
          source: 'schema' as const,
          active: true,
        }))
      ),
  ];

  const exportObjectsToExcel = () => {
    if (combinedObjects.length === 0) {
      alert("No objects to export");
      return;
    }

    const allFields = new Set<string>();
    combinedObjects.forEach((obj: any) => {
      Object.keys(obj).forEach(key => allFields.add(key));
    });

    const columnOrder = [
      'id', 'name', 'displayName', 'description', 'source', 'labelType', 'category', 'subcategory',
      'shapes', 'tags', 'color', 'hotkey', 'usageCount', 'createdBy', 'createdAt',
      'updatedBy', 'updatedAt', 'active'
    ];

    const orderedFields = [...columnOrder.filter(f => allFields.has(f)), ...Array.from(allFields).filter(f => !columnOrder.includes(f))];
    const headers = orderedFields.map(field => field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim());

    const rows = combinedObjects.map((obj: any) => orderedFields.map(field => {
      let value = obj[field];

      if (Array.isArray(value)) {
        value = value.join('; ');
      }

      if (value && typeof value === 'object' && !(value instanceof Date)) {
        value = JSON.stringify(value);
      }

      if (typeof value === 'boolean') {
        value = value ? 'Yes' : 'No';
      }

      if (value && (value.toDate || (typeof value === 'number' && value > 1000000000000))) {
        try {
          const date = value.toDate ? value.toDate() : new Date(value);
          value = date.toISOString();
        } catch {
          value = String(value);
        }
      }

      const stringValue = String(value ?? '');
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }));

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `objects_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredObjects = combinedObjects.filter(obj => {
    const matchesSearch = obj.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         obj.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         obj.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (typeof obj.labelType === 'string' && obj.labelType.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filter by parent category
    const matchesParentCategory = !parentCategoryFilter || obj.category === parentCategoryFilter;
    
    // Filter by subcategory (renamed from category)
    const matchesSubcategory = !categoryFilter || obj.subcategory === categoryFilter;
    
    // If parent category is selected, also filter subcategories to only show those belonging to that parent
    const matchesSubcategoryForParent = !parentCategoryFilter || !categoryFilter || 
      (() => {
        const subcategoryObj = objectCategories.find(cat => cat.id === categoryFilter);
        return subcategoryObj?.parentCategoryId === parentCategoryFilter;
      })();
    
    const matchesType = typeFilter === 'all' || 
                       (typeFilter === 'global' && obj.source === 'global') ||
                       (typeFilter === 'schema' && obj.source === 'schema');
    
    return matchesSearch && matchesParentCategory && matchesSubcategory && matchesSubcategoryForParent && matchesType && obj.active !== false;
  });

  // Create comprehensive list of all available subcategories for schema form
  // This includes all objectCategories plus any subcategories used in schemas
  // NOTE: We're now using subcategory (renamed from category)
  const allSchemaSubcategories = schemas.flatMap(schema => 
    schema.classes?.map(cls => (cls as any).subcategory || (cls as any).category).filter(Boolean) || []
  );
  
  const allGlobalObjectSubcategories = globalObjects.map(obj => obj.subcategory || obj.category).filter(Boolean);
  
  const allSubcategoryIds = Array.from(new Set([
    ...objectCategories.map(cat => cat.id),
    ...allSchemaSubcategories,
    ...allGlobalObjectSubcategories
  ]));
  
  const globalObjectSubcategories = allSubcategoryIds.map(subcategoryId => {
    const subcategory = objectCategories.find(cat => cat.id === subcategoryId);
    return subcategory || { id: subcategoryId, displayName: subcategoryId, name: subcategoryId };
  });

  // Helper function to remove undefined values from objects (Firestore doesn't allow undefined)
  const removeUndefined = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefined(item));
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj[key] !== undefined) {
          cleaned[key] = removeUndefined(obj[key]);
        }
      }
      return cleaned;
    }
    return obj;
  };

  const handleSaveSchema = async (schemaData: Partial<LabelSchema>) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Clean classes array - remove undefined category fields
      const cleanedClasses = (schemaData.classes || []).map(cls => {
        const cleaned: any = { ...cls };
        // Remove undefined category (parent category) but keep empty string if needed
        if (cleaned.category === undefined) {
          delete cleaned.category;
        }
        // Remove undefined subcategory but keep empty string if needed
        if (cleaned.subcategory === undefined) {
          delete cleaned.subcategory;
        }
        cleaned.attributes = cleaned.attributes ?? [];
        // Remove any other undefined fields
        Object.keys(cleaned).forEach(key => {
          if (cleaned[key] === undefined) {
            delete cleaned[key];
          }
        });
        return cleaned;
      });

      if (editingSchema) {
        // Editing existing schema
        const updatedSchema: any = {
          name: schemaData.name || '',
          scope: 'detection',
          guidelines: schemaData.guidelines || '',
          classes: cleanedClasses,
          featuredObjects: schemaData.featuredObjects || [],
          selectedGlobalCategories: schemaData.selectedGlobalCategories || [],
          imageAttributes: schemaData.imageAttributes || [],
          globalAttributes: schemaData.globalAttributes || [],
          version: schemaData.version || editingSchema.version,
          updatedAt: Date.now(),
          updatedBy: user.uid,
        };

        // Only include includeGlobalCategories if it's explicitly set
        if (schemaData.includeGlobalCategories !== undefined) {
          updatedSchema.includeGlobalCategories = schemaData.includeGlobalCategories;
        }

        // Remove any undefined values
        const cleanedSchema = removeUndefined(updatedSchema);

        // If creating new version, create a new document
        if (schemaData.version && schemaData.version > editingSchema.version) {
          const docRef = doc(collection(db, 'labelSchemas'));
          await setDoc(docRef, {
            ...cleanedSchema,
            createdAt: Date.now(),
            createdBy: user.uid,
            archived: false,
          });
        } else {
          // Update existing schema
          await updateDoc(doc(db, 'labelSchemas', editingSchema.id), cleanedSchema);
        }
      } else {
        // Creating new schema
        const newSchema: any = {
          name: schemaData.name || '',
          scope: 'detection',
          guidelines: schemaData.guidelines || '',
          classes: cleanedClasses,
          featuredObjects: schemaData.featuredObjects || [],
          selectedGlobalCategories: schemaData.selectedGlobalCategories || [],
          imageAttributes: schemaData.imageAttributes || [],
          globalAttributes: schemaData.globalAttributes || [],
          createdAt: Date.now(),
          createdBy: user.uid,
          updatedAt: Date.now(),
          updatedBy: user.uid,
          archived: false,
          version: schemaData.version || 1
        };

        // Only include includeGlobalCategories if it's explicitly set
        if (schemaData.includeGlobalCategories !== undefined) {
          newSchema.includeGlobalCategories = schemaData.includeGlobalCategories;
        }

        // Remove any undefined values
        const cleanedSchema = removeUndefined(newSchema);

        const docRef = doc(collection(db, 'labelSchemas'));
        await setDoc(docRef, cleanedSchema);
      }
      
      setShowCreateModal(false);
      setEditingSchema(null);
    } catch (error) {
      console.error('Error saving schema:', error);
      alert('Failed to save schema. Please try again.');
    }
  };

  const handleCreateObject = async (objectData: {
    name: string;
    displayName: string;
    description?: string;
    color: string;
    shapes: string[];
    hotkey?: string;
    category?: string; // NEW: top-level category (optional)
    subcategory: string; // RENAMED FROM category: subcategory (required)
    attributes?: string[];
  }) => {
    try {
      if (editingObject) {
        // Update existing object
        console.log('Updating global object:', objectData);
        // TODO: Implement update functionality
        console.log('✅ Global object updated successfully');
      } else {
        // Create new object
        console.log('Creating global object:', objectData);
        await createCategory(objectData);
        console.log('✅ Global object created successfully');
      }
      setShowCreateModal(false);
      setEditingObject(null);
    } catch (error) {
      console.error('❌ Error with global object:', error);
      alert(`Failed to ${editingObject ? 'update' : 'create'} global object: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteObject = async (objectId: string, objectName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the global object "${objectName}"?\n\nThis action cannot be undone and may affect schemas that use this object.`
    );
    
    if (!confirmed) return;
    
    try {
      console.log('Deleting global object:', objectId);
      await deleteCategory(objectId);
      console.log('✅ Global object deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting global object:', error);
      alert(`Failed to delete global object: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleSaveObjectEdit = async (objectData: any) => {
    try {
      if (editingCombinedObject?.source === 'global') {
        // Update global object
        console.log('Updating global object:', objectData);
        await updateCategory(editingCombinedObject.id, objectData);
        console.log('✅ Global object updated successfully');
      } else {
        // Update schema object
        const schemaId = editingCombinedObject.id.split(':')[0];
        const classId = editingCombinedObject.id.split(':')[1];
        const schema = schemas.find(s => s.id === schemaId);
        
        if (schema) {
          const updatedClasses = schema.classes?.map(cls => 
            cls.id === classId 
              ? { ...cls, ...objectData }
              : cls
          ) || [];
          
          const updatedSchema = {
            ...schema,
            classes: updatedClasses,
            updatedAt: Date.now(),
            updatedBy: 'admin' // TODO: Get from auth context
          };
          
          await updateDoc(doc(db, 'labelSchemas', schemaId), updatedSchema);
          console.log('✅ Schema object updated successfully');
        }
      }
      
      setShowObjectEditModal(false);
      setEditingCombinedObject(null);
    } catch (error) {
      console.error('❌ Error updating object:', error);
      alert(`Failed to update object: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteSchema = async (schemaId: string, schemaName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete the schema "${schemaName}"?\n\nThis action cannot be undone and will remove all versions of this schema.`
    );
    
    if (!confirmed) return;
    
    try {
      // Import deleteDoc from firebase/firestore
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'labelSchemas', schemaId));
      console.log('✅ Schema deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting schema:', error);
      alert('Failed to delete schema. Please try again.');
    }
  };

  const toggleArchive = async (schema: LabelSchema) => {
    try {
      await updateDoc(doc(db, 'labelSchemas', schema.id), {
        archived: !schema.archived
      });
    } catch (error) {
      console.error('Error toggling archive:', error);
      alert('Failed to update schema. Please try again.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Label Detection Setup</h1>
          <p className="text-gray-600 mt-2">
            Manage detection schemas and global objects for labeling tasks
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={activeTab === 'schemas' ? exportSchemasToExcel : exportObjectsToExcel}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
          >
            Export to Excel
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('schemas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'schemas'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Detection Schemas ({schemas.length})
          </button>
          <button
            onClick={() => setActiveTab('objects')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'objects'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Objects ({combinedObjects.filter(o => o.active !== false).length})
          </button>
        </nav>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          {activeTab === 'schemas' && (
            <select
              value={archiveFilter}
              onChange={(e) => setArchiveFilter(e.target.value as 'all' | 'active' | 'archived')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Schemas</option>
              <option value="active">Active Only</option>
              <option value="archived">Archived Only</option>
            </select>
          )}
          
          {activeTab === 'objects' && (
            <>
              <select
                value={parentCategoryFilter}
                onChange={(e) => {
                  setParentCategoryFilter(e.target.value);
                  // Clear subcategory filter if parent category changes
                  if (e.target.value) {
                    const selectedSubcategory = objectCategories.find(cat => cat.id === categoryFilter);
                    if (selectedSubcategory && selectedSubcategory.parentCategoryId !== e.target.value) {
                      setCategoryFilter('');
                    }
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {parentCategories.map((parent) => (
                  <option key={parent.id} value={parent.id}>
                    {parent.displayName}
                  </option>
                ))}
              </select>
              
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Subcategories</option>
                {objectCategories
                  .filter(cat => {
                    // If parent category is selected, only show subcategories that belong to it
                    if (parentCategoryFilter) {
                      return cat.parentCategoryId === parentCategoryFilter;
                    }
                    return true;
                  })
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.displayName}
                    </option>
                  ))}
              </select>
              
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as 'all' | 'global' | 'schema')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="global">Global Objects</option>
                <option value="schema">Schema Objects</option>
              </select>
            </>
          )}
        </div>

        {activeTab === 'schemas' && (
          <button
            onClick={() => {
              setEditingSchema(null);
              setEditingObject(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Schema
          </button>
        )}

        {activeTab === 'objects' && (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowCategoryManager(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Manage Categories
            </button>
            <button
              onClick={() => {
                setEditingObject(null);
                setEditingSchema(null);
                setShowCreateModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Object
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {activeTab === 'schemas' ? (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading schemas...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium mb-2">Error Loading Schemas</h3>
              <p className="text-red-600 text-sm mb-3">{error}</p>
              <div className="text-xs text-red-500">
                <p><strong>Debugging steps:</strong></p>
                <ol className="list-decimal list-inside space-y-1 mt-2">
                  <li>Open browser console (F12) to see detailed error logs</li>
                  <li>Check if you're logged in to Firebase</li>
                  <li>Verify Firestore rules allow read access to labelSchemas collection</li>
                  <li>Try refreshing the page</li>
                </ol>
              </div>
              <div className="flex space-x-3 mt-4">
                <button
                  onClick={reloadSchemas}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Retry
                </button>
                <button
                  onClick={() => {
                    console.log('🔍 Debug info:', {
                      schemas: schemas,
                      loading: loading,
                      error: error,
                      auth: getAuth().currentUser
                    });
                  }}
                  className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Debug Info
                </button>
                <button
                  onClick={async () => {
                    try {
                      console.log('🧪 Creating test data...');
                      await createTestData();
                      console.log('✅ Test data created successfully');
                      // Reload schemas to show the new data
                      reloadSchemas();
                    } catch (err) {
                      console.error('❌ Failed to create test data:', err);
                      alert('Failed to create test data. Check console for details.');
                    }
                  }}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Create Test Data
                </button>
              </div>
            </div>
          ) : filteredSchemas.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No schemas match your search' : 'No detection schemas found'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm 
                  ? 'Try adjusting your search terms or clear the search to see all schemas.' 
                  : 'Detection schemas define what objects can be labeled in your tasks. Create your first schema to get started.'
                }
              </p>
              <div className="flex justify-center space-x-3">
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Schema
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Schema Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Guidelines</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Classes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Version</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSchemas.map((schema) => (
                    <tr key={schema.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{schema.name}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={schema.guidelines || 'No guidelines provided'}>
                          {schema.guidelines || 'No guidelines provided'}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{schema.classes?.length || 0}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{schema.version || 1}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          schema.archived ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-600'
                        }`}>
                          {schema.archived ? 'Archived' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setEditingSchema(schema);
                              setShowCreateModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleArchive(schema)}
                            className="text-gray-600 hover:text-gray-900 font-medium"
                          >
                            {schema.archived ? 'Restore' : 'Archive'}
                          </button>
                          {schema.archived && (
                            <button
                              onClick={() => handleDeleteSchema(schema.id, schema.name)}
                              className="text-red-600 hover:text-red-900 font-medium"
                              title="Permanently delete this schema"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {objectsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading global objects...</p>
              </div>
            </div>
          ) : objectsError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium mb-2">Error Loading Objects</h3>
              <p className="text-red-600 text-sm">{objectsError}</p>
            </div>
          ) : filteredObjects.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No global objects found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating your first global object.'}
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Object
              </button>
            </div>
          ) : (
            <div className="bg-white shadow overflow-x-auto sm:rounded-md">
              <table className="min-w-[1600px] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Object
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Object ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Label Type / Schema
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subcategory
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shapes
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Color
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hotkey
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredObjects.map((obj) => (
                    <tr key={obj.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div 
                            className="w-4 h-4 rounded-full border-2 border-gray-300 mr-3 flex-shrink-0"
                            style={{ backgroundColor: obj.color }}
                          ></div>
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900 truncate">{obj.displayName}</div>
                            {obj.description && (
                              <div className="text-xs text-gray-500 truncate max-w-[220px]">{obj.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 font-mono">{obj.name}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          obj.source === 'global' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {obj.source === 'global' ? 'Global' : 'Schema'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          obj.source === 'global' 
                            ? 'bg-indigo-100 text-indigo-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {obj.labelType}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {obj.category && parentCategories.find(cat => cat.id === obj.category)
                            ? parentCategories.find(cat => cat.id === obj.category)?.displayName
                            : 'None'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {objectCategories.find(cat => cat.id === obj.subcategory)?.displayName || obj.subcategory || 'Unassigned'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {obj.shapes?.length ? obj.shapes.join(', ') : 'rectangle'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 max-w-xs">
                        {obj.description || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {obj.color ? (
                          <div className="flex items-center space-x-2">
                            <span className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: obj.color }}></span>
                            <span className="text-sm text-gray-600">{obj.color}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {obj.hotkey || '—'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {obj.tags && obj.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {obj.tags.map((tag) => (
                              <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {typeof obj.usageCount === 'number' ? obj.usageCount : '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {obj.createdBy || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(obj.createdAt)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(obj.updatedAt)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          obj.active === false ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                        }`}>
                          {obj.active === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingCombinedObject(obj);
                            setShowObjectEditModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          categories={objectCategories}
          loading={categoriesLoading}
          error={categoriesError}
          onCreateCategory={createObjectCategory}
          onUpdateCategory={updateObjectCategory}
          onDeleteCategory={deleteObjectCategory}
          parentCategories={parentCategories}
          parentLoading={parentLoading}
          parentError={parentError}
          onCreateParent={createParentCategory}
          onUpdateParent={updateParentCategory}
          onDeleteParent={deleteParentCategory}
          onClose={() => setShowCategoryManager(false)}
        />
      )}

      {/* Object Edit Modal */}
      {showObjectEditModal && editingCombinedObject && (
            <ObjectEditModal
              object={editingCombinedObject}
              categories={objectCategories}
              parentCategories={parentCategories}
              onSave={handleSaveObjectEdit}
              onCancel={() => {
                setShowObjectEditModal(false);
                setEditingCombinedObject(null);
              }}
            />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingSchema ? 'Edit Schema' : editingObject ? 'Edit Object' : activeTab === 'schemas' ? 'Create Detection Schema' : 'Create Global Object'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingSchema(null);
                    setEditingObject(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {activeTab === 'schemas' ? (
                <SchemaForm
                  schema={editingSchema || undefined}
                  categories={globalObjectSubcategories}
                  parentCategories={parentCategories}
                  globalObjects={globalObjects}
                  existingSchemas={schemas}
                  onSave={handleSaveSchema}
                  onCancel={() => {
                    setShowCreateModal(false);
                    setEditingSchema(null);
                    setEditingObject(null);
                  }}
                />
              ) : (
                <GlobalObjectForm
                  categories={objectCategories}
                  parentCategories={parentCategories}
                  object={editingObject || undefined}
                  onSave={handleCreateObject}
                  onCancel={() => {
                    setShowCreateModal(false);
                    setEditingObject(null);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Global Object Form Component
function GlobalObjectForm({ categories, parentCategories, object, onSave, onCancel }: { categories: any[]; parentCategories: any[]; object?: GlobalCategory; onSave: (data: any) => void; onCancel: () => void }) {
  const { attributes: labelAttributes, loading: attributesLoading } = useLabelAttributes();
  const instanceAttributes = labelAttributes.filter(
    (attr) => attr.level === 'instance' && attr.active !== false
  );

  const [formData, setFormData] = useState({
    name: object?.name || '',
    displayName: object?.displayName || '',
    description: object?.description || '',
    color: object?.color || '#3b82f6',
    shapes: object?.shapes || ['rectangle'],
    hotkey: object?.hotkey || '',
    category: object?.category || '', // NEW: top-level category (optional)
    subcategory: object?.subcategory || object?.category || '', // RENAMED: subcategory (required, fallback to old category for migration)
    attributes: object?.attributes || ([] as string[])
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const predefinedColors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280'
  ];

  const toggleAttribute = (attrName: string) => {
    setFormData((prev) => {
      const current = prev.attributes || [];
      const next = current.includes(attrName)
        ? current.filter((name) => name !== attrName)
        : [...current, attrName];
      return { ...prev, attributes: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    
    if (!formData.name || !formData.displayName || !formData.subcategory) {
      alert('Please fill in all required fields (Object ID, Display Name, and Subcategory)');
      return;
    }
    
    // Ensure shapes array is not empty
    if (!formData.shapes || formData.shapes.length === 0) {
      alert('Please select at least one shape');
      return;
    }
    
    setIsSubmitting(true);
    try {
      console.log('Calling onSave with:', formData);
      await onSave(formData);
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Object ID *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., cutting_board, knife, plate"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Internal ID (lowercase, underscores)</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Name *
          </label>
          <input
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Cutting Board, Knife, Plate"
            required
          />
          <p className="text-xs text-gray-500 mt-1">User-friendly name</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Brief description of the object"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category (optional)
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">None (Uncategorized)</option>
            {parentCategories.map((parent) => (
              <option key={parent.id} value={parent.id}>
                {parent.displayName}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Top-level category for grouping</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subcategory *
          </label>
          <select
            value={formData.subcategory}
            onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select a subcategory...</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.displayName}
              </option>
            ))}
          </select>
          {categories.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">
              No subcategories available. Create subcategories first.
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Attributes
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Attributes selected here apply to this object everywhere it is used.
        </p>
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
          {attributesLoading ? (
            <p className="text-xs text-gray-500">Loading attributes...</p>
          ) : instanceAttributes.length === 0 ? (
            <p className="text-xs text-gray-500">
              No instance-level attributes available. Create them in Attributes admin.
            </p>
          ) : (
            instanceAttributes.map((attr) => {
              const checked = formData.attributes.includes(attr.name);
              return (
                <label key={attr.name} className="flex items-start space-x-2 py-1">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAttribute(attr.name)}
                    className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="text-sm text-gray-900">
                      {attr.displayName}{' '}
                      <span className="font-mono text-xs text-gray-500">{attr.name}</span>
                    </span>
                    {attr.description && (
                      <span className="block text-xs text-gray-500 mt-0.5">{attr.description}</span>
                    )}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed Shapes
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['rectangle', 'polygon', 'point', 'line'].map((shape) => (
              <label key={shape} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.shapes.includes(shape)}
                  onChange={(e) => {
                    const currentShapes = formData.shapes;
                    const newShapes = e.target.checked
                      ? [...currentShapes, shape]
                      : currentShapes.filter(s => s !== shape);
                    setFormData({ ...formData, shapes: newShapes });
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 capitalize">{shape}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hotkey (optional)
          </label>
          <input
            type="text"
            value={formData.hotkey}
            onChange={(e) => setFormData({ ...formData, hotkey: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., 1, 2, 3, k"
          />
          <p className="text-xs text-gray-500 mt-1">Keyboard shortcut for quick selection</p>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Color
        </label>
        <div className="flex flex-wrap gap-2">
          {predefinedColors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => setFormData({ ...formData, color })}
              className={`w-8 h-8 rounded-full border-2 ${
                formData.color === color ? 'border-gray-400' : 'border-gray-200'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
        {object && (
          <button
            type="button"
            onClick={async () => {
              const confirmed = window.confirm(
                `Are you sure you want to archive "${object.displayName}"?\n\nThis will make it inactive but preserve all data.`
              );
              if (confirmed) {
                try {
                  // Archive the object by setting active to false
                  await onSave({ ...object, active: false });
                } catch (error) {
                  console.error('Error archiving object:', error);
                  alert('Failed to archive object');
                }
              }
            }}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Archive
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`px-4 py-2 rounded-lg transition-colors ${
            isSubmitting 
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isSubmitting ? (object ? 'Updating...' : 'Creating...') : (object ? 'Update Object' : 'Create Object')}
        </button>
      </div>
    </form>
  );
}
